import React, { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { Camera, X } from 'lucide-react';

export function loyaltyCodeFromQR(value) {
  const raw = String(value || '').trim();
  if (/^\d{4}$/.test(raw)) return raw;
  return raw.match(/^LOYALTY:[a-zA-Z0-9-]+:(\d{4})$/)?.[1] || null;
}

export default function LoyaltyScanner({ onDetected, disabled = false }) {
  const [active, setActive] = useState(false);
  const [message, setMessage] = useState('');
  const video = useRef(null);
  const callback = useRef(onDetected);
  callback.current = onDetected;

  useEffect(() => {
    if (!active) return;
    let cancelled = false, stream, timer;
    const stop = () => {
      cancelled = true;
      clearTimeout(timer);
      stream?.getTracks().forEach(track => track.stop());
    };
    const hidden = () => { if (document.hidden) { stop(); setActive(false); } };
    document.addEventListener('visibilitychange', hidden);
    const start = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw new Error('UNAVAILABLE');
        stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: { facingMode: { ideal: 'environment' } } });
        if (cancelled) { stream.getTracks().forEach(track => track.stop()); return; }
        video.current.srcObject = stream;
        await video.current.play();
        if (cancelled) return;
        setMessage('Placez le QR code dans le cadre.');
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) throw new Error('UNAVAILABLE');
        const scan = () => {
          if (cancelled) return;
          try {
          const player = video.current;
          if (player?.readyState >= 2 && player.videoWidth) {
            canvas.width = Math.min(640, player.videoWidth);
            canvas.height = Math.round(player.videoHeight * canvas.width / player.videoWidth);
            context.drawImage(player, 0, 0, canvas.width, canvas.height);
            const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
            const result = jsQR(pixels.data, pixels.width, pixels.height);
            if (result) {
              const code = loyaltyCodeFromQR(result.data);
              if (code) {
                stop(); setActive(false);
                setMessage('Carte scannée. Vérifiez le code puis validez le tampon.');
                callback.current(code);
                return;
              }
              setMessage('Ce QR code ne correspond pas à une carte de fidélité.');
            }
          }
          timer = setTimeout(scan, 180);
          } catch {
            stop(); setActive(false);
            setMessage('Lecture interrompue. Relancez la caméra ou saisissez les 4 chiffres ci-dessous.');
          }
        };
        scan();
      } catch (error) {
        if (cancelled) return;
        stop(); setActive(false);
        setMessage(error.name === 'NotAllowedError'
          ? 'Caméra non autorisée. Autorisez-la dans votre navigateur ou saisissez les 4 chiffres ci-dessous.'
          : 'Caméra indisponible. Utilisez un téléphone avec caméra ou saisissez les 4 chiffres ci-dessous.');
      }
    };
    start();
    return () => { stop(); document.removeEventListener('visibilitychange', hidden); };
  }, [active]);

  return <div className="korean-scanner">
    <button type="button" className="korean-button" disabled={disabled} onClick={() => { setMessage('Ouverture de la caméra…'); setActive(!active); }}>
      {active ? <X size={18} /> : <Camera size={18} />}{active ? 'Fermer la caméra' : 'Scanner une carte'}
    </button>
    {active && <div className="korean-scanner-frame"><video ref={video} muted playsInline aria-label="Aperçu de la caméra pour scanner la carte" /><span aria-hidden="true" /></div>}
    <p role="status">{message || 'Scannez la carte présentée par la cliente, ou saisissez son code ci-dessous.'}</p>
  </div>;
}
